import React, { useCallback, useEffect, useMemo, useRef } from 'react'

import {
  Edge,
  NodeChange,
  EdgeChange,
  NodeRemoveChange,
  EdgeRemoveChange,
  Connection,
  OnNodesChange,
  OnEdgesChange,
  useReactFlow,
  ReactFlowProvider
} from '@xyflow/react'

import Box from 'components_new/atoms/Box'

import DrawingBoard from 'components_new/molecules/sim/DrawingBoard'

import AggregateNode from 'components_new/organisms/sim/Node/Aggregate'
import CalculateNode from 'components_new/organisms/sim/Node/Calculate'
import CastNode from 'components_new/organisms/sim/Node/Cast'
import ClearNode from 'components_new/organisms/sim/Node/Clear'
import ConcatenateNode from 'components_new/organisms/sim/Node/Concatenate'
import DateParserNode from 'components_new/organisms/sim/Node/DateParser'
import FilterNode from 'components_new/organisms/sim/Node/Filter'
import InputNode from 'components_new/organisms/sim/Node/Input'
import LeftJoinNode from 'components_new/organisms/sim/Node/LeftJoin'
import LowerNode from 'components_new/organisms/sim/Node/Lower'
import MapNode from 'components_new/organisms/sim/Node/Map'
import ReplaceNode from 'components_new/organisms/sim/Node/Replace'
import TextReplaceNode from 'components_new/organisms/sim/Node/TextReplace'
import TimeDifferenceNode from 'components_new/organisms/sim/Node/TimeDifference'
import TrimNode from 'components_new/organisms/sim/Node/Trim'
import UniteNode from 'components_new/organisms/sim/Node/Unite'

import EdgeNode from 'components_new/organisms/sim/Edge'

import NodeType from 'types/transformation/enums/NodeType'
import { Node } from 'types/transformation/types/nodes'
import { SimTable } from 'types/transformation/types/tables'

import {
  clearReferences,
  getEdge,
  getInitNode,
  updateAffectedNodes
} from './utils'
import { getNodeBox, layoutBoard } from './layout'

import NodePalette from './NodePalette'

interface BoardProps {
  editMode: boolean
  // Bumped to lay the board out again once its nodes have been measured.
  layoutRequest: number
  nodes: Node[]
  edges: Edge[]
  onNodesChange: OnNodesChange<Node>
  onEdgesChange: OnEdgesChange<Edge>
  setEdges: React.Dispatch<React.SetStateAction<Edge[]>>
  setNodes: React.Dispatch<React.SetStateAction<Node[]>>
  table: SimTable
}

const BoardContent = (props: BoardProps) => {
  const {
    editMode,
    layoutRequest,
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    setEdges,
    setNodes,
    table
  } = props

  const paneRef = useRef<HTMLDivElement>(null)
  const laidOutRequest = useRef(layoutRequest)
  const { screenToFlowPosition, getInternalNode, fitView } =
    useReactFlow<Node>()

  // The layout is built from the nodes' rendered sizes and handle positions,
  // so a request waits until React Flow has measured every node.
  useEffect(() => {
    if (laidOutRequest.current === layoutRequest) return

    const measured = nodes.every(
      (node) => node.measured?.width && node.measured?.height
    )

    if (!measured) return

    laidOutRequest.current = layoutRequest

    const boxes = new Map(
      nodes.map((node) => [node.id, getNodeBox(node, getInternalNode(node.id))])
    )

    setNodes(layoutBoard(nodes, boxes))
    fitView({ duration: 300 })
  }, [layoutRequest, nodes, setNodes, getInternalNode, fitView])

  const nodeTypes = useMemo(() => {
    return {
      [NodeType.AGGREGATE]: function aggregate(props: any) {
        return <AggregateNode {...props} />
      },
      [NodeType.CALCULATE]: function calculate(props: any) {
        return <CalculateNode {...props} />
      },
      [NodeType.CAST]: function cast(props: any) {
        return <CastNode {...props} />
      },
      [NodeType.CLEAR]: function clear(props: any) {
        return <ClearNode {...props} />
      },
      [NodeType.CONCATENATE]: function concatenate(props: any) {
        return <ConcatenateNode {...props} />
      },
      [NodeType.DATE_PARSER]: function dateParser(props: any) {
        return <DateParserNode {...props} />
      },
      [NodeType.FILTER]: function filter(props: any) {
        return <FilterNode {...props} />
      },
      [NodeType.INPUT]: function input(props: any) {
        return <InputNode {...props} />
      },
      [NodeType.LEFT_JOIN]: function leftJoin(props: any) {
        return <LeftJoinNode {...props} />
      },
      [NodeType.LOWER]: function lower(props: any) {
        return <LowerNode {...props} />
      },
      [NodeType.MAP]: function output(props: any) {
        return <MapNode {...props} table={table} />
      },
      [NodeType.REPLACE]: function replace(props: any) {
        return <ReplaceNode {...props} />
      },
      [NodeType.TEXT_REPLACE]: function textReplace(props: any) {
        return <TextReplaceNode {...props} />
      },
      [NodeType.TIME_DIFFERENCE]: function timeDifference(props: any) {
        return <TimeDifferenceNode {...props} />
      },
      [NodeType.TRIM]: function trim(props: any) {
        return <TrimNode {...props} />
      },
      [NodeType.UNITE]: function unite(props: any) {
        return <UniteNode {...props} />
      },
      default: function d() {
        return null
      }
    }
  }, [table])

  const edgeTypes = useMemo(() => {
    return {
      CUSTOM: function custom(props: any) {
        return <EdgeNode {...props} />
      }
    }
  }, [])

  const handleNodesChange = useCallback(
    (changes: NodeChange<Node>[]) => {
      onNodesChange(changes)

      // Check for removed nodes
      const removedIds = changes
        .filter((c): c is NodeRemoveChange => c.type === 'remove')
        .map((c) => c.id)

      if (removedIds.length > 0) {
        // check if any nodes is referencing removed nodes.
        setNodes((nds) => updateAffectedNodes(nds, removedIds))
      }
    },
    [onNodesChange, setNodes]
  )

  const handleEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      onEdgesChange(changes)

      // Check for removed edges
      const removedEdges = changes
        .filter((c): c is EdgeRemoveChange => c.type === 'remove')
        .map((c) => c.id)

      const affectedEdges = edges.filter((item) =>
        removedEdges.includes(item.id)
      )

      const targetNodes = affectedEdges.map((edge) => edge.target)
      const sourceNodes = affectedEdges.map((edge) => edge.source)

      if (targetNodes.length > 0) {
        // check if any nodes is referencing removed node.
        setNodes((nds) =>
          nds.map((n) => {
            if (targetNodes.includes(n.id)) {
              const newData = { ...n.data }

              clearReferences(newData, sourceNodes, 'left')
              clearReferences(newData, sourceNodes, 'right')

              return { ...n, data: newData }
            }

            return n
          })
        )
      }
    },
    [onEdgesChange, edges, setNodes]
  )

  const handleOnConnect = useCallback(
    (connection: Connection) => {
      const edge = getEdge(connection)

      if (edge) {
        setEdges((_edges) => [..._edges, edge])

        // update affected node
        setNodes((_nodes) =>
          _nodes.map((node) => {
            if (node.id === edge.target) {
              // check which handle
              const attribute =
                edge.targetHandle === 'right' ? 'right_node' : 'left_node'

              return {
                ...node,
                data: {
                  ...node.data,
                  [attribute]: edge.source
                }
              }
            }

            return node
          })
        )
      }
    },
    [setNodes, setEdges]
  )

  const handleCreate = useCallback(
    (type: NodeType) => {
      const newNodeData = getInitNode(type, nodes)

      // place the new node at the center of the currently visible pane so
      // it stays in view regardless of the current zoom/pan
      const rect = paneRef.current?.getBoundingClientRect()
      const position = rect
        ? screenToFlowPosition({
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2
        })
        : { x: newNodeData.location_x, y: newNodeData.location_y }

      setNodes([
        ...nodes,
        {
          id: newNodeData.id,
          data: {
            ...newNodeData,
            location_x: position.x,
            location_y: position.y
          },
          position,
          type
        }
      ])
    },
    [nodes, setNodes, screenToFlowPosition]
  )

  return (
    <Box sx={{ display: 'flex', height: '100%' }}>
      {editMode ? <NodePalette onSelect={handleCreate} /> : null}
      <Box ref={paneRef} sx={{ flex: 1, minWidth: 0 }}>
        <DrawingBoard
          onNodesChange={handleNodesChange}
          onEdgesChange={handleEdgesChange}
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          onConnect={handleOnConnect}
          sx={
            editMode ? { border: '2px solid var(--primary-color)' } : undefined
          }
          editMode={editMode}
        />
      </Box>
    </Box>
  )
}

// ReactFlowProvider must wrap the content so BoardContent can read the live
// viewport (via useReactFlow) even though the <ReactFlow> element itself lives
// deeper, inside DrawingBoard.
const Board = (props: BoardProps) => (
  <ReactFlowProvider>
    <BoardContent {...props} />
  </ReactFlowProvider>
)

export default Board
